<template>
  <div
    class="setup-tasklist-page page-table-layout"
    data-testid="setup-tasklist"
  >
    <v-card
      data-testid="setup-tasklist__main-card"
      class="all-width flex-card shadow-off"
      :loading="isTabLoading"
      :disabled="templatesLoading && templatesInitialLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body">
        <div v-if="templatesInitialLoading" data-testid="setup-tasklist__skeleton-loader">
          <v-row class="mb-4">
            <v-col cols="6">
              <v-skeleton-loader type="heading" class="mb-2" />
              <v-skeleton-loader type="text" />
            </v-col>
            <v-col cols="6" class="d-flex justify-end">
              <v-skeleton-loader type="button" width="160" />
            </v-col>
          </v-row>
          <v-skeleton-loader type="table-heading, table-row-divider@8" />
        </div>

        <template v-else>
          <TaskListPageHeader
            :loading="templatesLoading"
            @refresh="handleRefresh"
          />

          <div class="setup-tasklist-page__tabs-row">
            <FcTabSwitcher
              variant="underline"
              :tabs="mainTabOptions"
              :value="mainTab"
              aria-label="Task Library sections"
              data-testid="setup-tasklist__main-tabs"
              @input="onMainTabChange"
            />
          </div>

          <div class="setup-tasklist-page__cycle-row d-flex align-center justify-space-between flex-wrap">
            <CycleTypeTabs
              :value="cycleType"
              :counts="cycleTabCounts"
              :disabled="isTabLoading"
              @change="onCycleTypeChange"
            />

            <TaskListToolbar
              v-if="mainTab === 'templates'"
              :headers="resolvedTableHeaders"
              :disabled="templatesLoading"
              :has-active-table-filter="hasActiveTableFilter"
              @apply-columns="tableHeaders = $event"
              @export="handleExport"
              @bulk-import="openBulkImport(BULK_IMPORT_MODES.IMPORT)"
              @add-template="handleAddTemplate"
              @clear-filter="clearTemplateFilters"
            />

            <CustomFieldsToolbar
              v-else
              :disabled="customFieldsLoading"
              @add-field="handleAddCustomField"
            />
          </div>

          <!-- Templates tab -->
          <template v-if="mainTab === 'templates'">
            <div
              v-if="isTabLoading"
              class="setup-tasklist-page__panel-loader"
              data-testid="setup-tasklist__templates-loader"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="40"
                width="3"
              />
            </div>

            <TaskListEmptyState
              v-else-if="isTemplatesTrueEmpty"
              variant="no-templates"
              :cycle-type="cycleType"
              :action-disabled="templatesLoading"
              @add-template="handleAddTemplate"
            />

            <TaskListEmptyState
              v-else-if="isTemplatesFilterEmpty"
              variant="filters"
              :cycle-type="cycleType"
              :action-disabled="templatesLoading"
              @action="clearTemplateFilters"
            />

            <template v-else>
              <div class="setup-tasklist-page__table-wrap flex-fill-table">
                <TaskListTable
                  ref="taskListTable"
                  :rows="paginatedTemplates"
                  :table-data="filteredTemplates"
                  :filter-options-data="formattedTemplates"
                  :headers="resolvedTableHeaders"
                  :selected-ids="selectedTemplateIds"
                  :items-per-page="itemsPerPage"
                  :loading="templatesLoading"
                  @update:selectedIds="setSelectedTemplateIds"
                  @row-click="handleRowClick"
                  @edit="handleEditTemplate"
                  @toggle-status="handleToggleTemplateStatus"
                  @filter-change="onTemplateFilterChange"
                  @filter-active-change="hasActiveTableFilter = $event"
                />

                <bulk-selection-bar
                  v-if="selectedTemplateIds.length > 0"
                  :selected-count="selectedTemplateIds.length"
                  section-label="EDIT FIELDS"
                  :actions="bulkSelectionActions"
                  :loading="bulkLoading"
                  test-id-prefix="setup-tasklist"
                  @action="onBulkSelectionAction"
                  @clear-selection="clearSelection"
                />
              </div>
            </template>
          </template>

          <!-- Custom Fields tab -->
          <template v-else>
            <div
              class="setup-tasklist-page__table-wrap flex-fill-table"
              data-testid="setup-tasklist__fields-panel"
            >
              <div
                v-if="isTabLoading"
                class="setup-tasklist-page__panel-loader"
                data-testid="setup-tasklist__fields-loader"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="40"
                  width="3"
                />
              </div>

              <TaskListEmptyState
                v-else-if="isCustomFieldsEmpty"
                variant="no-custom-fields"
                :cycle-type="cycleType"
                :action-disabled="customFieldsLoading"
                @action="handleAddCustomField"
              />

              <CustomFieldsTable
                v-else
                :rows="customFieldRows"
                :headers="customFieldHeaders"
                :loading="false"
                @edit="handleEditCustomField"
              />
            </div>
          </template>
        </template>
      </v-card-text>
    </v-card>

    <Pagination
      v-if="mainTab === 'templates' && !templatesInitialLoading && filteredTemplates.length"
      ref="taskListPagination"
      :key="paginationKey"
      :list="filteredTemplates"
      :itemsperpage="itemsPerPage"
      data-testid="setup-tasklist__pagination"
      @paginated-data="onPaginatedData"
    />

    <AddTemplateDialog
      ref="addTemplateDialog"
      :open="addTemplateDialog.open"
      :mode="addTemplateDialog.mode"
      :task-type="addTemplateDialog.taskType"
      :template-id="addTemplateDialog.templateId"
      :template="editingTemplate"
      :cycle-type="cycleType"
      :locations="locations"
      :users="users"
      :chart-of-accounts="chartOfAccounts"
      :form-meta="formMeta"
      :custom-fields="customColumnsForCycleType"
      :loading="templateDialogLoading"
      @save="handleSaveTemplate"
      @cancel="closeAddTaskTemplateDialog"
    />

    <CustomFieldDialog
      :open="customFieldDialog.open"
      :mode="customFieldDialog.mode"
      :form="customFieldForm"
      :existing-columns="customColumnsForCycleType"
      :loading="customFieldDialogLoading"
      @save="handleSaveCustomField"
      @cancel="closeCustomFieldDialog"
    />

    <BulkImportDialog
      ref="bulkImportDialog"
      :open="bulkImportOpen"
      :mode="bulkImportMode"
      :loading="bulkImportLoading"
      :cycle-type="cycleType"
      :templates="taskTemplatesForCycleType"
      :selected-templates="selectedTaskTemplates"
      :users="users"
      :locations="locations"
      :chart-of-accounts="chartOfAccounts"
      :categories="(formMeta && formMeta.categories) || []"
      :subcategories="(formMeta && formMeta.subcategories) || []"
      :custom-columns="customColumnsForCycleType"
      @update:loading="bulkImportLoading = $event"
      @success="closeBulkImport"
      @cancel="closeBulkImport"
    />

    <TaskListBulkDrawers
      ref="bulkDrawers"
      :selected-count="selectedTemplateIds.length"
      :selected-ids="selectedTemplateIds"
      :loading="bulkLoading"
      @confirm-due-days="handleBulkDueDaysConfirm"
      @confirm-threshold="handleBulkThresholdConfirm"
    />
  </div>
</template>

<script>
import './TaskList.scss';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { FcTabSwitcher, Pagination } from '../../../components';
import {
  CycleTypeTabs,
  TaskListPageHeader,
  TaskListToolbar,
  TaskListTable,
  TaskListBulkDrawers,
  TaskListEmptyState,
  CustomFieldsToolbar,
  CustomFieldsTable,
  CustomFieldDialog,
  AddTemplateDialog,
  BulkImportDialog,
} from './components';
import {
  MAIN_TABS,
  getTableHeaders,
  CUSTOM_FIELD_TABLE_HEADERS,
  ITEMS_PER_PAGE,
  BULK_IMPORT_MODES,
  getSystemCustomFields,
} from './utils/taskListConstants';
import {
  formatTemplateRow,
  formatCustomFieldRows,
  getTaskTypeConstants,
  isAccountReconciliation,
} from './utils/taskListHelpers';
import { downloadBulkEditTemplate } from './utils/bulkImportParse';
import { readTemplateSaveFieldErrors } from './utils/accessErrorsToFieldErrors';
import { FC_ROUTES } from '../../../utils/constants';

export default {
  name: 'SetupTaskList',

  components: {
    FcTabSwitcher,
    Pagination,
    CycleTypeTabs,
    TaskListPageHeader,
    TaskListToolbar,
    TaskListTable,
    TaskListBulkDrawers,
    TaskListEmptyState,
    CustomFieldsToolbar,
    CustomFieldsTable,
    CustomFieldDialog,
    AddTemplateDialog,
    BulkImportDialog,
  },

  data() {
    return {
      BULK_IMPORT_MODES,
      tableHeaders: getTableHeaders(this.$store.state.LocationHeader || 'Location').map((h) => ({
        ...h,
      })),
      customFieldHeaders: CUSTOM_FIELD_TABLE_HEADERS.map((h) => ({ ...h })),
      itemsPerPage: ITEMS_PER_PAGE,
      activeColumnFilters: {},
      hasActiveTableFilter: false,
      panelSwitchLoading: false,
      paginatedTemplates: [],
      paginationKey: 0,
      currentPage: 1,
      bulkLoading: false,
      bulkImportMode: BULK_IMPORT_MODES.IMPORT,
      bulkImportLoading: false,
      templateDialogLoading: false,
      customFieldDialogLoading: false,
      editingTemplate: null,
      customFieldForm: {},
    };
  },

  computed: {
    ...mapState('setup', {
      cycleType: (s) => s.cycleType || 'FBC',
      mainTab: (s) => s.mainTab || 'templates',
      templates: (s) => s.templates || [],
      templatesLoading: (s) => !!s.templatesLoading,
      templatesInitialLoading: (s) => !!s.templatesInitialLoading,
      templateCountsByCycleType: (s) => s.templateCountsByCycleType || {},
      customFieldCountsByCycleType: (s) => s.customFieldCountsByCycleType || {},
      selectedTemplateIds: (s) => s.selectedTemplateIds || [],
      customColumns: (s) => s.customColumns || [],
      customFieldsLoading: (s) => !!s.customFieldsLoading,
      addTemplateDialog: (s) => s.addTemplateDialog || { open: false },
      customFieldDialog: (s) => s.customFieldDialog || { open: false },
      locations: (s) => s.locations || [],
      users: (s) => s.users || [],
      chartOfAccounts: (s) => s.chartOfAccounts || [],
      formMeta: (s) => s.formMeta || {},
      bulkImportOpen: (s) => !!s.bulkImportOpen,
    }),

    ...mapGetters('setup', ['taskTemplatesForCycleType', 'customColumnsForCycleType', 'selectedTaskTemplates']),

    locationHeader() {
      return this.$store.state.LocationHeader || 'Location';
    },

    mainTabOptions() {
      return MAIN_TABS;
    },

    /**
     * Cycle chip counts must match the active main tab:
     * - Templates → template counts
     * - Fields → custom field counts (system rows are only shown when custom fields exist)
     */
    cycleTabCounts() {
      if (this.mainTab === 'customFields') {
        const custom = this.customFieldCountsByCycleType || {};
        const systemCount = getSystemCustomFields(this.locationHeader).length;
        return {
          FBC: (custom.FBC || 0) > 0 ? (custom.FBC || 0) + systemCount : 0,
          RM: (custom.RM || 0) > 0 ? (custom.RM || 0) + systemCount : 0,
          IA: (custom.IA || 0) > 0 ? (custom.IA || 0) + systemCount : 0,
          OTHERS: (custom.OTHERS || 0) > 0 ? (custom.OTHERS || 0) + systemCount : 0,
        };
      }
      return this.templateCountsByCycleType;
    },

    checklistType() {
      return this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
    },

    arType() {
      return this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },

    formattedTemplates() {
      return (this.taskTemplatesForCycleType || []).map((t) =>
        formatTemplateRow(t, this.locations, this.customColumnsForCycleType)
      );
    },

    resolvedTableHeaders() {
      const systemHeaderValues = new Set(
        getTableHeaders(this.locationHeader).map((h) => h.value)
      );
      const activeCustomValues = new Set(
        (this.customColumnsForCycleType || [])
          .filter((col) => col?.columnName && !col.isSystem)
          .map((col) => col.columnName)
      );
      const base = (this.tableHeaders || []).filter(
        (h) => systemHeaderValues.has(h.value) || activeCustomValues.has(h.value)
      );
      const existing = new Set(base.map((h) => h.value));
      const custom = (this.customColumnsForCycleType || [])
        .filter((col) => col?.columnName && !col.isSystem && !existing.has(col.columnName))
        .map((col) => ({
          text: col.displayName || col.displayText || col.columnName,
          value: col.columnName,
          visible: true,
          sortable: true,
          filterable: true,
        }));
      if (!custom.length) return base;
      const actionIdx = base.findIndex((h) => h.value === 'action');
      if (actionIdx === -1) return [...base, ...custom];
      return [...base.slice(0, actionIdx), ...custom, ...base.slice(actionIdx)];
    },

    filteredTemplates() {
      let rows = this.formattedTemplates;

      if (Object.keys(this.activeColumnFilters || {}).length && this.$nova?.filterByDynamicCriteria) {
        rows = this.$nova.filterByDynamicCriteria(rows, this.activeColumnFilters);
      }

      return rows;
    },

    customFieldRows() {
      return formatCustomFieldRows(
        this.customColumnsForCycleType,
        this.cycleType,
        this.locationHeader
      );
    },

    isTabLoading() {
      if (this.panelSwitchLoading) return true;
      // Keep the templates table mounted during refresh (enable/disable, save, etc.).
      // Full-panel loader only for initial bootstrap; inline table loading handles refresh.
      if (this.mainTab === 'templates') {
        return false;
      }
      return this.customFieldsLoading;
    },

    isTemplatesTrueEmpty() {
      return !this.templatesInitialLoading && !this.isTabLoading && this.formattedTemplates.length === 0;
    },

    isTemplatesFilterEmpty() {
      return (
        !this.isTemplatesTrueEmpty &&
        this.filteredTemplates.length === 0 &&
        this.hasActiveTableFilter
      );
    },

    isCustomFieldsEmpty() {
      return !this.isTabLoading && this.customColumnsForCycleType.length === 0;
    },

    hasARSelected() {
      return (this.selectedTaskTemplates || []).some((t) =>
        isAccountReconciliation(t.task_type, this)
      );
    },
    bulkSelectionActions() {
      const actions = [
        {
          id: 'bulk-update-duedays',
          label: 'Due Days',
          iconSrc: '/img/icons/fbc-icons/CalenderDateIcon.svg',
          testId: 'setup-tasklist__bulk-edit-duedays-btn',
          labelTestId: 'setup-tasklist__bulk-edit-duedays-label',
        },
      ];

      if (this.hasARSelected) {
        actions.push({
          id: 'bulk-edit-threshold',
          label: 'Threshold',
          iconSrc: '/img/icons/fbc-icons/ThresholdIcon.svg',
          testId: 'setup-tasklist__bulk-edit-threshold-btn',
          labelTestId: 'setup-tasklist__bulk-edit-threshold-label',
        });
      }

      actions.push({
        id: 'bulk-edit-tasks',
        label: 'Bulk Edit Task',
        iconSrc: '/img/icons/fbc-icons/BulkeditTaskIcon.svg',
        testId: 'setup-tasklist__bulk-edit-tasks-btn',
        labelTestId: 'setup-tasklist__bulk-edit-tasks-label',
      });

      return actions;
    },
  },

  watch: {
    locationHeader(label) {
      const header = (this.tableHeaders || []).find((h) => h.value === 'locationDisplay');
      if (header) header.text = label;
    },

    filteredTemplates: {
      handler() {
        // Keep the current page when rows refresh (e.g. enable/disable toggle).
        // Filter and cycle changes reset explicitly via their handlers.
        this.applyPagination({ resetPage: false });
      },
      deep: true,
    },

    addTemplateDialog: {
      deep: true,
      handler(dialog) {
        if (!dialog?.open) {
          this.editingTemplate = null;
          this.templateDialogLoading = false;
        }
      },
    },

    customFieldDialog: {
      deep: true,
      handler(dialog) {
        if (dialog?.open) {
          this.customFieldForm = dialog.column
            ? { ...dialog.column }
            : { cycleType: this.cycleType, displayName: '', dataType: '' };
        }
      },
    },
  },

  created() {
    this.init();
  },

  methods: {
    ...mapMutations('setup', [
      'setCycleType',
      'setMainTab',
      'setSelectedTemplateIds',
      'clearSelectedTemplateIds',
      'setBulkImportOpen',
    ]),

    ...mapActions('setup', [
      'fetchTaskLibraryBootstrap',
      'fetchTaskTemplates',
      'fetchCustomColumns',
      'refreshChecklistFormCategories',
      'createTaskTemplate',
      'updateTaskTemplate',
      'enableTaskTemplate',
      'disableTaskTemplate',
      'openAddTaskTemplateDialog',
      'openEditTaskTemplateDialog',
      'closeAddTaskTemplateDialog',
      'openCustomFieldDialog',
      'closeCustomFieldDialog',
      'createCustomColumn',
      'editCustomColumn',
      'updateDueDaysForBulkTaskTemplate',
      'updateSkipValidationsForBulkTaskTemplate',
      'fetchTaskTemplateDetail',
    ]),

    async init() {
      try {
        await this.fetchTaskLibraryBootstrap();
      } catch (err) {
        console.error('[SetupTaskList]: bootstrap error', err);
      }
    },

    async handleRefresh() {
      try {
        await Promise.all([
          this.fetchTaskTemplates(),
          this.fetchCustomColumns(),
          this.refreshChecklistFormCategories(),
        ]);
      } catch (err) {
        console.error('[SetupTaskList]: refresh error', err);
      }
    },

    async onMainTabChange(tab) {
      if (!tab || tab === this.mainTab) return;
      this.panelSwitchLoading = true;
      this.clearSelection();
      this.setMainTab(tab);
      try {
        await this.$nextTick();
        if (tab === 'customFields' && !(this.customColumns || []).length) {
          await this.fetchCustomColumns();
        }
      } catch (err) {
        console.error('[SetupTaskList]: main tab change error', err);
      } finally {
        this.panelSwitchLoading = false;
      }
    },

    async onCycleTypeChange(type) {
      if (!type || type === this.cycleType) return;
      this.panelSwitchLoading = true;
      this.clearSelection();
      if (this.mainTab === 'templates') {
        this.activeColumnFilters = {};
        this.hasActiveTableFilter = false;
      }
      this.setCycleType(type);
      try {
        await this.refreshChecklistFormCategories();
      } catch (err) {
        console.error('[SetupTaskList]: cycle category refresh error', err);
      } finally {
        this.panelSwitchLoading = false;
        if (this.mainTab === 'templates') {
          this.applyPagination({ resetPage: true });
        }
      }
    },

    applyPagination({ resetPage = false } = {}) {
      const maxPage = Math.ceil(this.filteredTemplates.length / this.itemsPerPage) || 1;
      let page = resetPage ? 1 : this.currentPage || 1;
      if (page > maxPage) {
        page = maxPage;
      }
      if (page < 1) {
        page = 1;
      }

      if (resetPage) {
        this.currentPage = 1;
        this.paginationKey += 1;
      } else {
        this.currentPage = page;
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      this.paginatedTemplates = this.filteredTemplates.slice(start, start + this.itemsPerPage);
    },

    syncPaginationComponent(page) {
      this.$nextTick(() => {
        this.$refs.taskListPagination?.setPage?.(page);
      });
    },

    onPaginatedData(info) {
      if (info && typeof info === 'object' && 'data' in info) {
        this.paginatedTemplates = Array.isArray(info.data) ? info.data : [];
        this.currentPage = info.page || 1;
      }
    },

    onTemplateFilterChange(filters) {
      const next = filters || {};
      const changed =
        JSON.stringify(next) !== JSON.stringify(this.activeColumnFilters || {});
      this.activeColumnFilters = next;
      if (changed) {
        // Filter apply/clear always drops the selection, even when every
        // previously selected row is still in the filtered list.
        this.clearSelection();
        this.applyPagination({ resetPage: true });
      }
    },

    clearTemplateFilters() {
      const hadFilters =
        Object.keys(this.activeColumnFilters || {}).length > 0 || this.hasActiveTableFilter;
      this.activeColumnFilters = {};
      this.hasActiveTableFilter = false;
      // Parent state is cleared before the table emits filter-change, so
      // onTemplateFilterChange will not see a change. Clear selection here.
      if (hadFilters) {
        this.clearSelection();
      }
      this.$refs.taskListTable?.clearTableFilter?.();
      this.applyPagination({ resetPage: true });
    },

    handleRowClick(item) {
      const rawId = item?._id;
      let id = '';
      if (rawId != null && typeof rawId === 'object') {
        id = String(rawId._id || rawId.$oid || rawId.id || '');
      } else if (rawId != null) {
        id = String(rawId);
      }
      if (!id || id === '[object Object]') return;

      const path = FC_ROUTES.SETUP_TASK_DETAIL(id);
      if (this.$route.path === path) return;

      // Always keep cid — missing cid can kick the user out of Financial Close.
      const cid = this.$route.query.cid || this.$store.state.client;
      const query = { ...(this.$route.query || {}) };
      if (cid) query.cid = String(cid);

      this.$router
        .push({ name: 'fc-setup-task-detail', params: { id }, query })
        .catch((err) => {
          if (err?.name === 'NavigationDuplicated') return;
          // Fallback if named route is not registered yet (HMR / late module load).
          this.$router.push({ path, query }).catch((fallbackErr) => {
            if (fallbackErr?.name === 'NavigationDuplicated') return;
            console.error('[SetupTaskList]: navigate to task detail failed', fallbackErr || err);
          });
        });
    },

    handleAddTemplate(taskType) {
      this.openAddTaskTemplateDialog({ taskType: taskType || this.checklistType });
    },

    async handleEditTemplate(item) {
      const id = item?._id;
      if (!id) return;
      const taskType = item.task_type || null;
      // Open drawer in loading state; hydrate only after GET with API-shaped payload.
      this.editingTemplate = null;
      this.templateDialogLoading = true;
      this.openEditTaskTemplateDialog({ templateId: id, taskType });
      try {
        this.editingTemplate = await this.fetchTaskTemplateDetail(id);
      } catch (err) {
        console.error('[SetupTaskList]: load template error', err);
        const raw = (this.templates || []).find((t) => String(t._id) === String(id));
        this.editingTemplate = raw ? { ...raw } : null;
      } finally {
        this.templateDialogLoading = false;
      }
    },

    async handleSaveTemplate({ id, data, mode }) {
      this.templateDialogLoading = true;
      try {
        if (mode === 'edit' && id) {
          await this.updateTaskTemplate({ id, data });
        } else {
          await this.createTaskTemplate(data);
        }
        this.closeAddTaskTemplateDialog();
        this.editingTemplate = null;
      } catch (err) {
        const fieldErrors = readTemplateSaveFieldErrors(err);
        if (fieldErrors) {
          this.$refs.addTemplateDialog?.applyServerFieldErrors?.(fieldErrors);
        }
        console.error('[SetupTaskList]: save template error', err);
      } finally {
        this.templateDialogLoading = false;
      }
    },

    async handleToggleTemplateStatus({ item, enabled }) {
      if (!item?._id) return;
      const savedPage =
        this.$refs.taskListPagination?.currentPage || this.currentPage || 1;
      try {
        if (enabled) await this.enableTaskTemplate(item._id);
        else await this.disableTaskTemplate(item._id);
        this.applyPagination({ resetPage: false });
        this.syncPaginationComponent(savedPage);
      } catch (err) {
        console.error('[SetupTaskList]: toggle status error', err);
      }
    },

    handleAddCustomField() {
      this.openCustomFieldDialog({ mode: 'add', column: null });
    },

    handleEditCustomField(column) {
      this.openCustomFieldDialog({ mode: 'edit', column });
    },

    async handleSaveCustomField(columnDetails) {
      this.customFieldDialogLoading = true;
      try {
        if (this.customFieldDialog.mode === 'edit') {
          await this.editCustomColumn({
            data: columnDetails,
            query: { _id: columnDetails._id },
          });
        } else {
          await this.createCustomColumn(columnDetails);
          if (columnDetails.cycleType && columnDetails.cycleType !== this.cycleType) {
            this.setCycleType(columnDetails.cycleType);
          }
        }
        this.closeCustomFieldDialog();
      } catch (err) {
        console.error('[SetupTaskList]: save custom field error', err);
      } finally {
        this.customFieldDialogLoading = false;
      }
    },

    clearSelection() {
      this.clearSelectedTemplateIds();
      this.$refs.taskListTable?.syncHeaderCheckbox?.();
    },

    onBulkSelectionAction(actionId) {
      if (actionId === 'bulk-update-duedays') {
        this.openDueDaysDrawer();
        return;
      }
      if (actionId === 'bulk-edit-threshold') {
        this.openThresholdDrawer();
        return;
      }
      if (actionId === 'bulk-edit-tasks') {
        this.openBulkImport(BULK_IMPORT_MODES.BULK_EDIT);
      }
    },

    openDueDaysDrawer() {
      this.$refs.bulkDrawers?.openDueDaysDrawer();
    },

    openThresholdDrawer() {
      this.$refs.bulkDrawers?.openThresholdDrawer();
    },

    async handleBulkDueDaysConfirm({ ids, duedays }) {
      this.bulkLoading = true;
      try {
        await this.updateDueDaysForBulkTaskTemplate({ ids, duedays });
        this.$refs.bulkDrawers?.closeAfterSuccess();
        this.clearSelection();
      } catch (err) {
        console.error('[SetupTaskList]: bulk due days error', err);
        this.$store.commit(
          'sbError',
          err?.response?.data?.message || err?.message || 'Due days update failed'
        );
      } finally {
        this.bulkLoading = false;
      }
    },

    async handleBulkThresholdConfirm({ ids, enforceThreshold }) {
      this.bulkLoading = true;
      try {
        await this.updateSkipValidationsForBulkTaskTemplate({ ids, enforceThreshold });
        this.$refs.bulkDrawers?.closeAfterSuccess();
        this.clearSelection();
      } catch (err) {
        console.error('[SetupTaskList]: bulk threshold error', err);
        this.$store.commit(
          'sbError',
          err?.response?.data?.message || err?.message || 'Threshold update failed'
        );
      } finally {
        this.bulkLoading = false;
      }
    },

    openBulkImport(mode) {
      this.bulkImportMode = mode || BULK_IMPORT_MODES.IMPORT;
      this.setBulkImportOpen(true);
    },

    closeBulkImport() {
      this.setBulkImportOpen(false);
      this.bulkImportLoading = false;
    },

    async handleExport() {
      const templates = this.filteredTemplates || [];
      if (!templates.length) {
        this.$store.commit('sbInfo', 'No task templates to export.');
        return;
      }

      try {
        await downloadBulkEditTemplate({
          templates,
          users: this.users,
          locations: this.locations,
          customColumns: this.customColumnsForCycleType,
          cycleType: this.cycleType,
          taskTypes: getTaskTypeConstants(this),
          locationHeader: this.locationHeader,
          title: 'Tasklist List',
          filename: `Task_Library_${this.cycleType || 'ALL'}.xlsx`,
        });
        this.$store.commit('sbSuccess', 'Task templates exported successfully.');
      } catch (err) {
        console.error('[SetupTaskList]: handleExport error', err);
        this.$store.commit(
          'sbError',
          err?.message || 'Failed to export task templates.'
        );
      }
    },
  },
};
</script>
